// src/pages/BlogPost.jsx
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getArticleBySlug, buildImageUrl } from "../../lib/strapi";
import RichTextRenderer from "../../ui/RichTextRenderer";
import { PostSkeleton } from "../../ui/Skeletons";
import CategoryBadge from "../../ui/CategoryBadge";
import AuthorBadge from "../../ui/AuthorBadge";
import MediaBlock from "../../ui/MediaBlock";

const pick = (item) => (item && item.attributes ? item.attributes : item || {});
const pickMedia = (m) => (m && m.data && m.data.attributes ? m.data.attributes : m || {});

export default function BlogPost() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [post, setPost] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const data = await getArticleBySlug(slug, { populate: "*" });
        if (mounted) setPost(data);
      } catch (err) {
        console.error(err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [slug]);

  if (loading) return <PostSkeleton />;
  if (!post) return <main className="max-w-3xl mx-auto px-4 py-10">Post not found.</main>;

  const a = pick(post);
  const cover = pickMedia(a.cover);
  const author = pick(a.author) || null;
  const category = pick(a.category) || null;

  const authorName = author?.name || author?.username || null;
  const categoryName = category?.name || category?.title || category?.slug || null;

  // Render dynamic blocks if present (Strapi v5 uses dynamic zones a lot)
  const blocks = Array.isArray(a.blocks) ? a.blocks : [];

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Link to="/help/all" className="text-sm text-gray-500 hover:underline">
            ← Back
          </Link>
          {categoryName ? <CategoryBadge>{categoryName}</CategoryBadge> : null}
          {authorName ? <AuthorBadge>{authorName}</AuthorBadge> : null}
        </div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-4">{a.title}</h1>
        {a.description && <p className="text-gray-600">{a.description}</p>}
      </div>

      {cover?.url && (
        <figure className="mb-8 overflow-hidden rounded-2xl border bg-gray-50">
          <img
            src={buildImageUrl(cover)}
            alt={a.title || "Article cover"}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </figure>
      )}

      {/* If you have a plain HTML 'body' field, render it */}
      {a.body && <RichTextRenderer html={a.body} />}

      {/* Render Dynamic Zone 'blocks' if present */}
      {blocks.length > 0 && (
        <section className="mt-6 space-y-6">
          {blocks.map((block, idx) => {
            const b = pick(block);

            // shared.rich-text → { body: "<p>...</p>" }
            if (b.__component === "shared.rich-text" && b.body) {
              return <RichTextRenderer key={`rt-${idx}-${b.id || idx}`} html={b.body} />;
            }

            // shared.media → might contain `file` or `media` or `files`
            if (b.__component === "shared.media") {
              const file =
                pickMedia(b.file) ||
                pickMedia(b.media) ||
                pickMedia(b.image) ||
                null;
              if (file?.url) {
                return <MediaBlock key={`mb-${idx}-${b.id || idx}`} file={file} />;
              }
              return null;
            }

            // shared.slider → optional, handle if images array exists
            if (b.__component === "shared.slider" && Array.isArray(b.images)) {
              return (
                <div key={`sl-${idx}-${b.id || idx}`} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {b.images.map((img, i) => {
                    const f = pickMedia(img);
                    return f?.url ? (
                      <figure key={i} className="rounded-2xl overflow-hidden border bg-gray-50">
                        <img
                          src={buildImageUrl(f)}
                          alt={f.alternativeText || f.name || "Slide image"}
                          className="w-full h-auto object-cover"
                          loading="lazy"
                        />
                      </figure>
                    ) : null;
                  })}
                </div>
              );
            }

            // Unknown block → skip gracefully
            return null;
          })}
        </section>
      )}

      {/* Optional extra media array/field */}
      {Array.isArray(a?.media?.data) && a.media.data.length > 0 && (
        <div className="mt-8 space-y-6">
          {a.media.data.map((item) => {
            const file = pickMedia(item);
            return <MediaBlock key={item.id} file={file} />;
          })}
        </div>
      )}
    </main>
  );
}
